import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { StatusBadge, ServiceBadge } from '../components/StatusBadge';
import { InlineStatusEditor } from '../components/InlineStatusEditor';
import { HoverReveal } from '../components/HoverReveal';
import { DoubleScroll } from '../components/DoubleScroll';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Search,
  MoreVertical,
  Eye,
  Trash2,
  FileSpreadsheet,
  Loader2,
  ClipboardList,
  Settings2,
  ChevronDown,
  Download,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  UserPlus,
  ArrowLeft,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COMPANY_NAMES = {
  edp: 'EDP', galp: 'Galp', endesa: 'Endesa', iberdrola: 'Iberdrola',
  gold: 'Gold Energy', repsol: 'Repsol', yes: 'Yes', meo_energia: 'Meo Energia',
  nos: 'NOS', meo: 'MEO', vodafone: 'Vodafone',
  nos_securitas: 'NOS Securitas', prosegur: 'Prosegur'
};

const ALL_COLUMNS = [
  { key: 'codigo', label: 'Codigo', default: true },
  { key: 'nome_cliente', label: 'Nome Cliente', default: true },
  { key: 'nif', label: 'NIF', default: true },
  { key: 'tipo_produto', label: 'Tipo Produto', default: true },
  { key: 'tipo_segmento', label: 'Tipo Segmento', default: true },
  { key: 'potencia', label: 'Potência', default: true },
  { key: 'company_name', label: 'Empresa', default: true },
  { key: 'status', label: 'Estado', default: true },
  { key: 'cpe', label: 'CPE', default: false },
  { key: 'cui', label: 'CUI', default: false },
  { key: 'campanha', label: 'Campanha', default: false },
  { key: 'morada_fornecimento', label: 'Morada Fornecimento', default: false },
  { key: 'nr_porta_fornecimento', label: 'Nr Porta', default: false },
  { key: 'andar_fornecimento', label: 'Andar', default: false },
  { key: 'codigo_postal_fornecimento', label: 'Codigo Postal', default: false },
  { key: 'localidade', label: 'Localidade', default: false },
  { key: 'telefones_str', label: 'Telefone', default: false },
  { key: 'emails_str', label: 'Email', default: false },
  { key: 'iban', label: 'IBAN', default: false },
  { key: 'fatura_eletronica', label: 'Fatura Eletronica', default: false },
  { key: 'debito_direto', label: 'Debito Direto', default: false },
  { key: 'data_assinatura', label: 'Data Assinatura', default: false },
  { key: 'created_at', label: 'Criado em', default: true },
  { key: 'assigned_to_name', label: 'Proprietario', default: true },
  { key: 'angariador_name', label: 'Angariador', default: true },
  { key: 'created_by_name', label: 'Criado por', default: false },
  { key: 'partner_name', label: 'Parceiro', default: false },
  { key: 'codigo_registo_cu', label: 'Codigo Registo C.U.', default: false },
  { key: 'nome_registo_cu', label: 'Nome Registo C.U.', default: false },
  { key: 'agendamento', label: 'Agendamento', default: false },
  { key: 'observacoes', label: 'Observacoes', default: false },
];

// Min-widths in px per column. With table-layout: auto the browser
// distributes remaining horizontal space evenly across the visible columns,
// and headers/cells stay readable thanks to a sensible minimum.
const COL_MIN = {
  codigo: 56,
  nome_cliente: 160,
  nif: 110,
  tipo_produto: 90,
  tipo_segmento: 100,
  potencia: 95,
  company_name: 95,
  status: 110,
  cpe: 130,
  cui: 130,
  campanha: 110,
  morada_fornecimento: 140,
  nr_porta_fornecimento: 60,
  andar_fornecimento: 60,
  codigo_postal_fornecimento: 100,
  localidade: 110,
  telefones_str: 110,
  emails_str: 140,
  iban: 140,
  fatura_eletronica: 70,
  debito_direto: 70,
  data_assinatura: 105,
  created_at: 105,
  assigned_to_name: 130,
  angariador_name: 130,
  created_by_name: 130,
  partner_name: 110,
  codigo_registo_cu: 120,
  nome_registo_cu: 130,
  agendamento: 105,
  observacoes: 160,
};
const colMinStyle = (key) => ({ minWidth: `${COL_MIN[key] || 100}px` });

// Smart tooltip: only shows tooltip when text is actually truncated
const TruncCell = ({ children, className = '', mono = false }) => {
  const ref = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const text = typeof children === 'string' ? children : '';

  useEffect(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [children]);

  const baseClass = `truncate block ${mono ? 'text-xs font-mono' : 'text-sm'} ${className}`;

  if (!isTruncated || !text) {
    return <span ref={ref} className={baseClass}>{children}</span>;
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span ref={ref} className={baseClass}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg break-all">
        {text}
      </TooltipContent>
    </Tooltip>
  );
};

export const ContractsAllPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isSuperAdmin, hasPerm, previewPartner } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Column whitelist enforced for restricted partners (Credível-style):
  // when their partner has excel_export_restrito=true with a list of allowed columns,
  // they CANNOT see/select columns outside that list (column picker is filtered).
  // super_admin/gestor are NEVER restricted. Preview-as-partner respects the previewed partner.
  const columnWhitelist = React.useMemo(() => {
    if (['super_admin', 'gestor'].includes(user?.role) && !previewPartner) return null;
    const perms = previewPartner?.permissions || user?.partner_permissions || {};
    if (perms.excel_export_restrito === true && Array.isArray(perms.excel_export_columns) && perms.excel_export_columns.length > 0) {
      return new Set(perms.excel_export_columns);
    }
    return null;
  }, [user, previewPartner]);

  const visibleColumnDefs = React.useMemo(() => {
    if (!columnWhitelist) return ALL_COLUMNS;
    // Always keep "codigo" and "nome_cliente" so the row is identifiable
    return ALL_COLUMNS.filter(c => columnWhitelist.has(c.key) || c.key === 'codigo' || c.key === 'nome_cliente');
  }, [columnWhitelist]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [companyFilter, setCompanyFilter] = useState(searchParams.get('company') || 'all');
  const [proprietarioFilter, setProprietarioFilter] = useState(searchParams.get('proprietario') || 'all');
  const [partnerFilter, setPartnerFilter] = useState(searchParams.get('partner_id') || 'all');
  const [statuses, setStatuses] = useState({});
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || '');

  // Sorting
  const [sortKey, setSortKey] = useState(searchParams.get('sort_key') || 'codigo');
  const [sortDir, setSortDir] = useState(searchParams.get('sort_dir') || 'desc');

  // Pagination
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('page_size') || '25', 10));

  // Persist all filters/sort/page to URL so the back navigation from
  // ContractDetail restores the exact list state the user was viewing.
  useEffect(() => {
    const next = {};
    if (searchQuery) next.q = searchQuery;
    if (statusFilter !== 'all') next.status = statusFilter;
    if (companyFilter !== 'all') next.company = companyFilter;
    if (proprietarioFilter !== 'all') next.proprietario = proprietarioFilter;
    if (partnerFilter !== 'all') next.partner_id = partnerFilter;
    if (dateFrom) next.date_from = dateFrom;
    if (dateTo) next.date_to = dateTo;
    if (sortKey && sortKey !== 'codigo') next.sort_key = sortKey;
    if (sortDir && sortDir !== 'desc') next.sort_dir = sortDir;
    if (page > 1) next.page = String(page);
    if (pageSize !== 25) next.page_size = String(pageSize);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, companyFilter, proprietarioFilter, partnerFilter, dateFrom, dateTo, sortKey, sortDir, page, pageSize]);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('crm_visible_columns');
    if (saved) {
      const cols = JSON.parse(saved);
      let updated = cols;
      // Migrate: add assigned_to_name if not present
      if (!updated.includes('assigned_to_name')) {
        updated = [...updated, 'assigned_to_name'];
      }
      // Migrate: add angariador_name by default so users see it after deploy
      if (!updated.includes('angariador_name')) {
        updated = [...updated, 'angariador_name'];
      }
      if (updated !== cols) {
        localStorage.setItem('crm_visible_columns', JSON.stringify(updated));
      }
      return updated;
    }
    return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // Enforce whitelist on the current visibleColumns (restricted partners only)
  const effectiveVisibleColumns = React.useMemo(() => {
    if (!columnWhitelist) return visibleColumns;
    return visibleColumns.filter(k => columnWhitelist.has(k) || k === 'codigo' || k === 'nome_cliente');
  }, [visibleColumns, columnWhitelist]);

  // Export
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter, companyFilter, partnerFilter]);
  
  // Global refresh button (header)
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('crm:refresh', handler);
    return () => window.removeEventListener('crm:refresh', handler);
  }, []);

  // Debounce search query → re-fetch from backend (so it matches RAW data, not masked)
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem('crm_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter !== 'all') params.company = companyFilter;
      if (partnerFilter !== 'all') params.partner_id = partnerFilter;
      if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();

      const isAdmin = ['super_admin', 'gestor', 'admin', 'parceiro'].includes(user?.role);
      const requests = [
        axios.get(`${API_URL}/contracts`, { params }),
        axios.get(`${API_URL}/config/statuses`),
      ];
      if (isAdmin) {
        requests.push(axios.get(`${API_URL}/users`));
        requests.push(axios.get(`${API_URL}/partners`));
      }

      const responses = await Promise.all(requests);
      setContracts(responses[0].data);
      setStatuses(responses[1].data);
      if (isAdmin) {
        setUsers(responses[2]?.data || []);
        if (responses[3]) setPartners(responses[3].data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja eliminar este contrato?')) return;
    try {
      await axios.delete(`${API_URL}/contracts/${id}`);
      toast.success('Contrato eliminado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao eliminar contrato');
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Use the CURRENT list filters (WYSIWYG) — what you see is what you export.
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (companyFilter !== 'all') params.company = companyFilter;
      if (partnerFilter !== 'all') params.partner_id = partnerFilter;
      if (proprietarioFilter !== 'all') params.assigned_to = proprietarioFilter;
      if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await axios.get(`${API_URL}/contracts/export/excel`, {
        params: { ...params, visible_columns: effectiveVisibleColumns.join(',') },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = response.headers['content-disposition']
        ?.split('filename=')[1]?.replace(/"/g, '') || 'contratos.xlsx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy', { locale: pt });
    } catch {
      return dateStr?.substring(0, 10) || '-';
    }
  };

  const toggleColumn = (key) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const resetColumns = () => {
    setVisibleColumns(ALL_COLUMNS.filter(c => c.default).map(c => c.key));
  };

  const filteredContracts = contracts.filter(contract => {
    // NOTE: search is now handled by the backend (matches RAW unmasked data)
    if (dateFrom && contract.created_at && contract.created_at < dateFrom) return false;
    if (dateTo && contract.created_at && contract.created_at > dateTo + 'T23:59:59') return false;
    if (proprietarioFilter !== 'all' && contract.assigned_to !== proprietarioFilter) return false;
    return true;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedContracts = [...filteredContracts].sort((a, b) => {
    let valA = a[sortKey];
    let valB = b[sortKey];

    // Handle numeric sort for codigo
    if (sortKey === 'codigo') {
      valA = parseInt(valA) || 0;
      valB = parseInt(valB) || 0;
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedContracts.length / pageSize);
  const paginatedContracts = sortedContracts.slice((page - 1) * pageSize, page * pageSize);

  // Reset page when filters change
  const resetPage = () => setPage(1);

  const uniqueCompanies = [...new Set(contracts.map(c => c.company))].sort();
  const uniqueCampaigns = [...new Set(contracts.map(c => c.campanha).filter(Boolean))].sort();

  const getCellValue = (contract, key) => {
    switch (key) {
      case 'status':
        return (
          <InlineStatusEditor
            contract={contract}
            onUpdated={(id, newStatus) => {
              setContracts(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
            }}
          />
        );
      case 'company_name':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#2D3E6F]/10 text-[#2D3E6F] truncate">
            {contract.company_name || COMPANY_NAMES[contract.company] || contract.company}
          </span>
        );
      case 'potencia': {
        const p = contract.potencia;
        if (!p) return <span className="text-slate-300">-</span>;
        const isLow = parseFloat(p) < 3.45;
        return (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${isLow ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {isLow && <span className="mr-1">⚠️</span>}{p} kVA
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              {isLow ? 'Potência inferior a 3,45 kVA — NÃO comissionada' : 'Potência comissionável'}
            </TooltipContent>
          </Tooltip>
        );
      }
      case 'nome_cliente':
        return (
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Link
                to={`/contracts/${contract.service_category}/${contract.company}/${contract.id}`}
                state={{ returnTo: location.pathname + location.search }}
                className="font-medium text-[#2D3E6F] hover:text-[#4DD4E8] hover:underline truncate block"
              >
                {contract.nome_cliente}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              {contract.nome_cliente}
            </TooltipContent>
          </Tooltip>
        );
      case 'created_at':
        return <TruncCell className="text-slate-500">{formatDate(contract.created_at)}</TruncCell>;
      case 'data_assinatura':
        return <TruncCell className="text-slate-500">{contract.data_assinatura || '-'}</TruncCell>;
      case 'created_by_name':
        return <TruncCell className="text-slate-500">{contract.created_by_name || '-'}</TruncCell>;
      case 'assigned_to_name':
        return <TruncCell className="font-medium text-[#4DD4E8]">{contract.assigned_to_name || '-'}</TruncCell>;
      case 'angariador_name':
        return contract.angariador_name
          ? <TruncCell><span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-full text-xs font-medium">{contract.angariador_name}</span></TruncCell>
          : <TruncCell className="text-slate-300">—</TruncCell>;
      case 'tipo_produto':
        return <TruncCell>{(contract.tipo_produto || '').replace('_', ' + ')}</TruncCell>;
      case 'telefones_str': {
        const tels = (contract.telefones || []).map(t => t.numero).filter(Boolean).join(', ') || '-';
        const isMasked = typeof tels === 'string' && tels.includes('*');
        return isMasked ? (
          <TruncCell><HoverReveal contractId={contract.id} field="telefones">{tels}</HoverReveal></TruncCell>
        ) : <TruncCell>{tels}</TruncCell>;
      }
      case 'emails_str': {
        const emails = (contract.emails || []).filter(Boolean).join(', ') || '-';
        const isMasked = typeof emails === 'string' && emails.includes('*');
        return isMasked ? (
          <TruncCell><HoverReveal contractId={contract.id} field="emails">{emails}</HoverReveal></TruncCell>
        ) : <TruncCell>{emails}</TruncCell>;
      }
      case 'fatura_eletronica':
        return <span className="text-sm">{contract.fatura_eletronica === 'sim' ? 'Sim' : 'Nao'}</span>;
      case 'debito_direto':
        return <span className="text-sm">{contract.debito_direto === 'sim' ? 'Sim' : 'Nao'}</span>;
      case 'localidade':
        return <TruncCell>{contract.localidade_fornecimento || contract.localidade || '-'}</TruncCell>;
      case 'cpe': {
        const v = contract.cpe || '-';
        const isMasked = typeof v === 'string' && v.includes('*');
        return isMasked ? (
          <TruncCell mono><HoverReveal contractId={contract.id} field="cpe">{v}</HoverReveal></TruncCell>
        ) : <TruncCell mono>{v}</TruncCell>;
      }
      case 'cui': {
        const v = contract.cui || '-';
        const isMasked = typeof v === 'string' && v.includes('*');
        return isMasked ? (
          <TruncCell mono><HoverReveal contractId={contract.id} field="cui">{v}</HoverReveal></TruncCell>
        ) : <TruncCell mono>{v}</TruncCell>;
      }
      case 'nif': {
        const v = contract.nif || '-';
        const isMasked = typeof v === 'string' && v.includes('*');
        return isMasked ? (
          <TruncCell mono><HoverReveal contractId={contract.id} field="nif">{v}</HoverReveal></TruncCell>
        ) : <TruncCell mono>{v}</TruncCell>;
      }
      case 'iban': {
        const v = contract.iban || '-';
        const isMasked = typeof v === 'string' && v.includes('*');
        return isMasked ? (
          <TruncCell mono><HoverReveal contractId={contract.id} field="iban">{v}</HoverReveal></TruncCell>
        ) : <TruncCell mono>{v}</TruncCell>;
      }
      case 'observacoes':
        return <TruncCell>{contract.tipo_pendencia || '-'}</TruncCell>;
      case 'morada_fornecimento':
        return <TruncCell>{contract.morada_fornecimento || '-'}</TruncCell>;
      case 'partner_name':
        return <TruncCell>{contract.partner_name || '-'}</TruncCell>;
      default:
        return <TruncCell>{contract[key] || '-'}</TruncCell>;
    }
  };

  return (
    <TooltipProvider>
    <Layout>
      <div className="space-y-6" data-testid="contracts-all-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-[#2D3E6F]" />
              Contratos Geral
            </h1>
            <p className="text-slate-500 mt-1">
              {sortedContracts.length} contratos de todas as empresas
              {(dateFrom || dateTo) && <span className="text-[#4DD4E8]"> (filtrado por data)</span>}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Column Picker */}
            <DropdownMenu open={showColumnPicker} onOpenChange={setShowColumnPicker}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full gap-2" data-testid="column-picker-button">
                  <Settings2 className="w-4 h-4" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 max-h-80 overflow-y-auto">
                {visibleColumnDefs.map(col => (
                  <DropdownMenuItem key={col.key} onSelect={(e) => e.preventDefault()}>
                    <label className="flex items-center gap-2 w-full cursor-pointer">
                      <Checkbox
                        checked={effectiveVisibleColumns.includes(col.key)}
                        onCheckedChange={() => toggleColumn(col.key)}
                        data-testid={`col-toggle-${col.key}`}
                      />
                      <span className="text-sm">{col.label}</span>
                    </label>
                  </DropdownMenuItem>
                ))}
                {columnWhitelist && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem disabled className="opacity-70">
                      <span className="text-xs text-slate-500">🔒 Colunas restritas pelo administrador</span>
                    </DropdownMenuItem>
                  </>
                )}
                {!columnWhitelist && <DropdownMenuSeparator />}
                {!columnWhitelist && (
                  <DropdownMenuItem onSelect={resetColumns}>
                    <span className="text-sm text-slate-500">Reset</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full gap-2" data-testid="acoes-button">
                  Ações
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleExportExcel()} disabled={exporting}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {exporting ? 'A exportar…' : 'Exportar Excel'}
                </DropdownMenuItem>
                {(user?.role === 'super_admin' || user?.role === 'gestor') && (
                  <DropdownMenuItem
                    onSelect={async () => {
                      if (!window.confirm('Procurar nos contratos sem angariador e atribuir o angariador da lead correspondente (mesmo NIF)?')) return;
                      try {
                        const res = await axios.post(`${API_URL}/admin/contracts/backfill-angariador`);
                        const d = res.data;
                        toast.success(`${d.updated} de ${d.total_checked} contratos atualizados`);
                        fetchData();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Erro ao atualizar angariadores');
                      }
                    }}
                    data-testid="backfill-angariador-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Atualizar Angariadores
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters */}
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome, NIF, código ou empresa..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); resetPage(); }}
                  className="pl-10 h-11 rounded-lg"
                  data-testid="search-all-contracts-input"
                />
              </div>
              <Select value={companyFilter} onValueChange={(v) => { setCompanyFilter(v); resetPage(); }}>
                <SelectTrigger className="w-full md:w-48 h-11 rounded-lg" data-testid="company-filter">
                  <SelectValue placeholder="Empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  {uniqueCompanies.map(key => (
                    <SelectItem key={key} value={key}>
                      {COMPANY_NAMES[key] || key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); resetPage(); }}>
                <SelectTrigger className="w-full md:w-48 h-11 rounded-lg" data-testid="status-filter-all">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {Object.entries(statuses).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {['super_admin', 'gestor', 'admin', 'parceiro'].includes(user?.role) && partners.length > 0 && (
                <Select value={partnerFilter} onValueChange={(v) => { setPartnerFilter(v); resetPage(); }}>
                  <SelectTrigger className="w-full md:w-48 h-11 rounded-lg" data-testid="partner-filter">
                    <SelectValue placeholder="Parceiro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os parceiros</SelectItem>
                    {partners.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={proprietarioFilter} onValueChange={(v) => { setProprietarioFilter(v); resetPage(); }}>
                <SelectTrigger className="w-full md:w-52 h-11 rounded-lg" data-testid="proprietario-filter">
                  <SelectValue placeholder="Proprietário" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os proprietários</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Date range filter */}
            <div className="flex flex-col md:flex-row gap-4 mt-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500 whitespace-nowrap">De:</span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg w-40 text-sm"
                  data-testid="date-from-filter"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 whitespace-nowrap">Até:</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); resetPage(); }}
                  className="h-9 rounded-lg w-40 text-sm"
                  data-testid="date-to-filter"
                />
              </div>
              {(dateFrom || dateTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDateFrom(''); setDateTo(''); resetPage(); }}
                  className="text-xs text-slate-500 hover:text-slate-700 h-9"
                  data-testid="clear-dates-btn"
                >
                  Limpar datas
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-slate-100">
          <div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : sortedContracts.length === 0 ? (
              <div className="text-center py-16">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-400">Nenhum contrato encontrado</p>
              </div>
            ) : (
              <DoubleScroll>
                <Table className="w-full" style={{ tableLayout: 'auto' }}>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    {ALL_COLUMNS.filter(c => effectiveVisibleColumns.includes(c.key)).map(col => (
                      <TableHead
                        key={col.key}
                        className="font-semibold text-xs uppercase cursor-pointer select-none hover:bg-slate-100/80 transition-colors whitespace-nowrap"
                        style={colMinStyle(col.key)}
                        onClick={() => handleSort(col.key)}
                        data-testid={`sort-${col.key}`}
                      >
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          {col.label}
                          {sortKey === col.key ? (
                            sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-[#2D3E6F] shrink-0" /> : <ArrowDown className="w-3 h-3 text-[#2D3E6F] shrink-0" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-300 shrink-0" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedContracts.map((contract, index) => (
                    <TableRow
                      key={contract.id}
                      className="hover:bg-slate-50/50"
                      data-testid={`all-contract-row-${index}`}
                    >
                      {ALL_COLUMNS.filter(c => effectiveVisibleColumns.includes(c.key)).map(col => (
                        <TableCell key={col.key} className="truncate" style={colMinStyle(col.key)}>
                          {getCellValue(contract, col.key)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.service_category}/${contract.company}/${contract.id}`, { state: { returnTo: location.pathname + location.search } })}>
                              <Eye className="w-4 h-4 mr-2" />Ver detalhes
                            </DropdownMenuItem>
                            {isSuperAdmin() && (
                            <DropdownMenuItem onClick={() => handleDelete(contract.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Eliminar
                            </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </DoubleScroll>
            )}
          </div>
          {sortedContracts.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-slate-100 gap-3">
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-500">
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, sortedContracts.length)} de {sortedContracts.length}
                </p>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 text-xs" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400">por página</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="h-8 px-2 text-xs"
                  data-testid="page-first"
                >
                  «
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 px-3 text-xs"
                  data-testid="page-prev"
                >
                  Anterior
                </Button>
                <span className="text-sm text-slate-600 px-3 font-medium">
                  {page} / {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-8 px-3 text-xs"
                  data-testid="page-next"
                >
                  Seguinte
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="h-8 px-2 text-xs"
                  data-testid="page-last"
                >
                  »
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
    </TooltipProvider>
  );
};
